import {
  Button,
  Description,
  Divider,
  Form,
  Icon,
  Input,
  InputGroup,
  Link,
  Modal,
  Select
} from '@opengov/component-library/dist';
import { BasicColumnTypes } from '@opengov/ogql-types';

import {
  createConstantReportColumn,
  EOgqlInfixColumnOperators,
  EReportColumnTypes,
  EReportConstant,
  IConstantReportColumn,
  IInfixReportColumn,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { debounce } from 'lodash';
import React from 'react';

import { IReportLocale } from '../../../../common/locale';
import {
  CalculationPreviewTable,
  ICalculationPreviewTable
} from '../../../basicCalculation/components/CalculationPreviewTable';
import { EAllowedCalculationTypes, ECalculationCustomValues } from '../../../basicCalculation/types';
import { percentageInputFormatToStorageFormat, percentageStorageFormatToInputFormat } from '../utilities';

import { OPERATOR_MENU_HEIGHT, VALUE_1_MENU_HEIGHT, VALUE_2_MENU_HEIGHT } from './basicCalculationConstants';
import styles from './CreateBasicCalculationModal.scss';
import { NavigateFunction, useNavigate } from 'react-router-dom';

export interface ICreateBasicCalculationModal {
  reportLocaleText: IReportLocale;
  value1Options: any;
  value2Options: any;
  calculationPreviewTableProps: ICalculationPreviewTable;
  columns: IReportColumn[];
  basicCalculation: IInfixReportColumn;
  operationOptions: any[];
  closeModal(): void;
  goBack(): void;
  fetchPreviewData(): void;
  updateValue1(value: IReportColumn): void;
  updateValue1CustomValue(value: string): void;
  updateValue2(value: IReportColumn): void;
  updateValue2CustomValue(value: string): void;
  updateOperator(value: EOgqlInfixColumnOperators): void;
  updateColumnName(name: string): void;
  saveColumn(column: IInfixReportColumn, navigate: NavigateFunction): void;
}

export class CreateBasicCalculationModal extends React.Component<ICreateBasicCalculationModal> {
  constructor(props) {
    super(props);
    this.getCalculationPreviewTableData = debounce(this.getCalculationPreviewTableData, 500);
  }

  getCalculationPreviewTableData() {
    this.props.fetchPreviewData();
  }

  componentDidUpdate(prevProps) {
    const { basicCalculation } = this.props;
    const value2Complete =
      basicCalculation.rightColumn.columnType === EReportColumnTypes.constant
        ? !!basicCalculation.rightColumn.value
        : !!basicCalculation.rightColumn.name;
    if (basicCalculation !== prevProps.basicCalculation && value2Complete) {
      this.getCalculationPreviewTableData();
    }
  }

  handleValueSelect(value: string, updaterFunction: any) {
    const {
      columns,
      reportLocaleText: {
        calculations: {
          basicCalculationModal: {
            customValueNumeric,
            customValuePercentage,
            customValueDateDay,
            customValueDateMonth,
            customValueDateYear
          }
        }
      }
    } = this.props;
    const placeholderConstantNumeric = createConstantReportColumn({
      name: ECalculationCustomValues.Numeric,
      displayName: customValueNumeric,
      type: BasicColumnTypes.Numeric,
      unit: EReportConstant.Number
    });
    const placeholderConstantPercentage = createConstantReportColumn({
      name: ECalculationCustomValues.Percentage,
      displayName: customValuePercentage,
      type: BasicColumnTypes.Percent,
      unit: EReportConstant.Percentage
    });

    switch (value) {
      case ECalculationCustomValues.Numeric:
        updaterFunction(placeholderConstantNumeric);
        break;
      case ECalculationCustomValues.Percentage:
        updaterFunction(placeholderConstantPercentage);
        break;
      case ECalculationCustomValues.Year:
        updaterFunction(
          createConstantReportColumn({
            name: ECalculationCustomValues.Year,
            displayName: customValueDateYear,
            type: BasicColumnTypes.Numeric,
            unit: EReportConstant.Year
          })
        );
        break;
      case ECalculationCustomValues.Month:
        updaterFunction(
          createConstantReportColumn({
            name: ECalculationCustomValues.Month,
            displayName: customValueDateMonth,
            type: BasicColumnTypes.Numeric,
            unit: EReportConstant.Month
          })
        );
        break;
      case ECalculationCustomValues.Day:
        updaterFunction(
          createConstantReportColumn({
            name: ECalculationCustomValues.Day,
            displayName: customValueDateDay,
            type: BasicColumnTypes.Numeric,
            unit: EReportConstant.Day
          })
        );
        break;
      default: {
        const column = columns.find((c) => c.name === value);
        updaterFunction(column);
      }
    }
  }
  renderCustomValueInput(customValueColumn: IConstantReportColumn<any>, updaterFunction: any, fieldName: string) {
    const {
      reportLocaleText: {
        calculations: {
          basicCalculationModal: {
            customValueDateDay,
            customValueDateMonth,
            customValueDateYear,
            customValueNumeric,
            customValuePercentage
          }
        }
      }
    } = this.props;

    let inputLabel;
    let inputUpdaterFunction = updaterFunction;
    switch (customValueColumn.unit) {
      case EReportConstant.Percentage:
        inputLabel = customValuePercentage;
        inputUpdaterFunction = (value) => updaterFunction(percentageInputFormatToStorageFormat(value.toString()));
        return (
          <Form.Item label={inputLabel}>
            <Form.CharacterCounter
              maxCount={14}
              currentCount={
                customValueColumn.value ? percentageStorageFormatToInputFormat(customValueColumn.value).length : 0
              }
            />
            <InputGroup>
              <Input.ParsedNumber
                maxLength={14} //calculations tends to get really slow after 14
                dataTest={`${customValueColumn.name}-input-${fieldName}`}
                onChange={inputUpdaterFunction}
                value={percentageStorageFormatToInputFormat(customValueColumn.value) || ''}
              />
              <InputGroup.Suffix>
                <InputGroup.Icon icon="datatype-percentage" />
              </InputGroup.Suffix>
            </InputGroup>
          </Form.Item>
        );
      case EReportConstant.Year:
        inputLabel = customValueDateYear;
        break;
      case EReportConstant.Month:
        inputLabel = customValueDateMonth;
        break;
      case EReportConstant.Day:
        inputLabel = customValueDateDay;
        break;
      case EReportConstant.Number:
      default:
        inputLabel = customValueNumeric;
    }
    return (
      <Form.Item label={inputLabel}>
        <Form.CharacterCounter
          maxCount={14}
          currentCount={customValueColumn.value ? customValueColumn.value.toString().length : 0}
        />
        <InputGroup>
          <Input.ParsedNumber
            maxLength={14}
            dataTest={`${customValueColumn.name}-input-${fieldName}`}
            onChange={inputUpdaterFunction}
            value={customValueColumn.value || ''}
          />
        </InputGroup>
      </Form.Item>
    );
  }
  getIconComponent = (operator) => {
    switch (operator) {
      case EOgqlInfixColumnOperators.multiply:
        return <Icon icon="operator-multiply" />;
      case EOgqlInfixColumnOperators.divide:
        return <Icon icon="operator-divide" />;
      case EOgqlInfixColumnOperators.subtract:
        return <Icon icon="operator-subtract" />;
      case EOgqlInfixColumnOperators.add:
      default:
        return <Icon icon="operator-add" />;
    }
  };
  render() {
    const {
      reportLocaleText: {
        calculations: {
          basicCalculationModal: {
            calculateNewColumn,
            columnName,
            create,
            value1,
            value2,
            operation,
            newColumnName,
            returnToStart,
            preview,
            previewMessage,
            selectValue
          }
        }
      },
      saveColumn,
      calculationPreviewTableProps,
      basicCalculation,
      closeModal,
      goBack,
      operationOptions,
      value1Options,
      value2Options,
      updateValue1,
      updateValue1CustomValue,
      updateValue2,
      updateValue2CustomValue,
      updateOperator,
      updateColumnName
    } = this.props;
    const value1Complete =
      basicCalculation.leftColumn.columnType === EReportColumnTypes.constant
        ? !!basicCalculation.leftColumn.value
        : !!basicCalculation.leftColumn.name;
    const value2Complete =
      basicCalculation.rightColumn.columnType === EReportColumnTypes.constant
        ? !!basicCalculation.rightColumn.value
        : !!basicCalculation.rightColumn.name;
    const showCustomValue1 = basicCalculation.leftColumn.columnType === EReportColumnTypes.constant;
    const showCustomValue2 = basicCalculation.rightColumn.columnType === EReportColumnTypes.constant;
    return (
      <Modal.Work.Wide title={calculateNewColumn} onClose={closeModal}>
        <div className={styles.columnWrapper}>
          <div className={styles.leftColumn}>
            <fieldset>
              <Form.Item label={value1}>
                <Select
                  value={basicCalculation.leftColumn.name}
                  placeholder={selectValue}
                  dataTest="value-1-select-menu"
                  maxMenuHeight={VALUE_1_MENU_HEIGHT}
                  onChange={(value) => {
                    this.handleValueSelect(value, updateValue1);
                  }}
                >
                  {[
                    ...[...(Object as any).values(EAllowedCalculationTypes), 'custom'].reduce((options, columnType) => {
                      if (value1Options[columnType]) {
                        if (columnType !== 'custom') {
                          options.push([
                            <Select.Label
                              key={`value-1-${columnType}-section-label`}
                              text={(columnType as string).toUpperCase()}
                              disabled={value1Options[columnType].disabled}
                            />,
                            ...value1Options[columnType].items.map((option, i) => {
                              return (
                                <Select.Option
                                  disabled={option.disabled}
                                  dataTest={`value-1-${columnType}-option-${i}`}
                                  key={`value-1-option-${option.value}`}
                                  label={option.label}
                                  value={option.value}
                                  secondaryLabel={option.secondaryLabel}
                                />
                              );
                            }),
                            <Select.Divider key={`show-option-${columnType}-divider`} />
                          ]);
                        } else {
                          options.push([
                            ...value1Options[columnType].items.map((option, i) => {
                              return (
                                <Select.Option
                                  disabled={option.disabled}
                                  dataTest={`value-1-${columnType}-option-${i}`}
                                  key={`value-1-option-${option.value}`}
                                  label={option.label}
                                  value={option.value}
                                />
                              );
                            })
                          ]);
                        }
                      }
                      return options;
                    }, [])
                  ]}
                </Select>
              </Form.Item>
              {showCustomValue1 &&
                this.renderCustomValueInput(basicCalculation.leftColumn as any, updateValue1CustomValue, 'value-1')}
              <Form.Item label={operation}>
                <Select
                  maxMenuHeight={OPERATOR_MENU_HEIGHT}
                  value={basicCalculation.operator}
                  placeholder={operation}
                  disabled={!value1Complete}
                  dataTest="operation-select-menu"
                  onChange={updateOperator}
                >
                  {operationOptions.map((option) => (
                    <Select.Option
                      prefixIcon={this.getIconComponent(option.value)}
                      disabled={option.disabled}
                      dataTest={`operation-option-${option.value}`}
                      key={`operation-option-${option.value}`}
                      label={option.label}
                      value={option.value}
                    />
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label={value2}>
                <Select
                  value={basicCalculation.rightColumn.name}
                  placeholder={selectValue}
                  disabled={!value1Complete || !basicCalculation.operator}
                  maxMenuHeight={VALUE_2_MENU_HEIGHT}
                  dataTest="value-2-select-menu"
                  onChange={(value) => {
                    this.handleValueSelect(value, updateValue2);
                  }}
                >
                  {[
                    ...[...(Object as any).values(EAllowedCalculationTypes), 'custom'].reduce((options, columnType) => {
                      if (value2Options[columnType]) {
                        if (columnType !== 'custom') {
                          options.push([
                            <Select.Label
                              key={`value-2-${columnType}-section-label`}
                              text={(columnType as string).toUpperCase()}
                              disabled={value2Options[columnType].disabled}
                            />,
                            ...value2Options[columnType].items.map((option, i) => {
                              return (
                                <Select.Option
                                  disabled={option.disabled}
                                  dataTest={`value-2-${columnType}-option-${i}`}
                                  key={`value-2-option-${option.value}`}
                                  label={option.label}
                                  value={option.value}
                                  secondaryLabel={option.secondaryLabel}
                                />
                              );
                            }),
                            <Select.Divider key={`value-2-option-${columnType}-divider`} />
                          ]);
                        } else {
                          options.push([
                            ...value2Options[columnType].items.map((option, i) => {
                              return (
                                <Select.Option
                                  disabled={option.disabled}
                                  dataTest={`value-2-${columnType}-option-${i}`}
                                  key={`value-2-option-${option.value}`}
                                  label={option.label}
                                  value={option.value}
                                />
                              );
                            })
                          ]);
                        }
                      }
                      return options;
                    }, [])
                  ]}
                </Select>
              </Form.Item>
              {showCustomValue2 &&
                this.renderCustomValueInput(basicCalculation.rightColumn as any, updateValue2CustomValue, 'value-2')}
              <Divider.Horizontal />
              <Form.Item label={newColumnName}>
                <Form.CharacterCounter maxCount={30} currentCount={basicCalculation.displayName.length} />
                <InputGroup>
                  <InputGroup.Prefix>
                    <InputGroup.Label htmlFor="newColumnName">=</InputGroup.Label>
                  </InputGroup.Prefix>
                  <Input.Text
                    dataTest="new-column-name-input"
                    id="newColumnName"
                    placeholder={columnName}
                    value={basicCalculation.displayName}
                    onChange={updateColumnName}
                  />
                </InputGroup>
              </Form.Item>
            </fieldset>
          </div>
          <div className={styles.flexVertical}>
            <Divider.Vertical />
          </div>
          <div className={styles.rightColumn}>
            <fieldset>
              <Form.Item label={preview}>
                {basicCalculation.operator && value1Complete && value2Complete ? (
                  <div className={styles.previewTable} data-test="column-preview-table">
                    <CalculationPreviewTable {...calculationPreviewTableProps} />
                  </div>
                ) : (
                  <div className={styles.previewMessage} data-test="column-preview-message">
                    <Description>{previewMessage}</Description>
                  </div>
                )}
              </Form.Item>
            </fieldset>
          </div>
        </div>
        <div role="button" className={styles.controlsWrapper}>
          <div className={styles.returnToStart}>
            <Icon icon="arrow-left-small" />
            <Link.Quiet href="#" text={returnToStart} onClick={goBack} />
          </div>
          <CreateBasicCalculationButton
            saveColumn={saveColumn}
            basicCalculation={basicCalculation}
            value1Complete={value1Complete}
            value2Complete={value2Complete}
            create={create}
          />
        </div>
      </Modal.Work.Wide>
    );
  }
}

export function CreateBasicCalculationButton({
  saveColumn,
  basicCalculation,
  value1Complete,
  value2Complete,
  create
}: {
  saveColumn: (column: IInfixReportColumn, navigate: NavigateFunction) => void;
  basicCalculation: IInfixReportColumn;
  value1Complete: boolean;
  value2Complete: boolean;
  create: string;
}) {
  const navigate = useNavigate();

  return (
    <Button
      variant="primary"
      onClick={() => {
        saveColumn(basicCalculation, navigate);
      }}
      dataTest="create-basic-calculation-button"
      disabled={
        !basicCalculation.operator ||
        !value1Complete ||
        !value2Complete ||
        !basicCalculation.displayName ||
        basicCalculation.displayName?.length > 30
      }
    >
      {create}
    </Button>
  );
}
